import dayjs from "dayjs";
import { camelCase, find, mapKeys } from "lodash";

import Sentry from "@similarweb/sentry-logger";

import { requestCustomCategoriesSuccess } from "app/actions/customCategoriesActions"; // Adjust path
import { requestCustomCategories } from "app/actions/customCategoriesActions"; // Adjust path
import type { CategoryId, ICategory } from "app/common/services/categoryService.types";
import { getSharedCustomCategories as getSharedCustomCategoriesSharedDashboard } from "app/common/services/sharedAssetsService";
import type { INotification } from "app/components/sharingAssetModal/types";
import { ESharingState } from "app/components/sharingAssetModal/types";
import { DefaultFetchService, NoCacheHeaders } from "app/services/fetchService";
import { SwRedux } from "app/services/SwRedux";
import queryClient from "single-spa/react-app/query-client";

interface ICustomCategoryRecord {
    id: string;
    name: string;
    domains: string[];
    addedTime: string;
    lastUpdated: string;
    userId?: number;
    categoryHash: string;
    isShared: boolean;
    sharedWithUsers: Array<string>;
    sharedWithAccounts: Array<string>;
    segments?: string[];
}

const EXCLUDE_CAMEL_CASE_KEYS: Array<string> = ["filters"];
const COPIED_INDUSTRY_SUFFIX = "Copy";

export interface ICustomCategoriesAndShared {
    userCustomIndustries: ICustomCategories;
    sharedCustomIndustries: ICustomCategories;
}

export type ICustomCategories = ICustomCategoryRecord[];

export interface ICustomCategoryResponse {
    AddedTime: string;
    CategoryHash: string;
    CategoryType: string;
    CreationType: number;
    Domains: string[];
    Id: string;
    LastUpdated: string;
    Name: string;
    UserId: string;
}

export const CATEGORIES_TO_DISPLAY = 200;
const CUSTOM_CATEGORIES_ENDPOINT = "api/userdata/customindustries";
export const CUSTOM_CATEGORY_PREFIX = "*";

let customIndustries: ICustomCategories = [];
let sharedCustomIndustries: ICustomCategories = [];
let enrichedSharedCustomIndustries: ICategory[] = [];

const updateGlobalCustomCategoriesState = () => {
    const swRedux = SwRedux.getInstance();
    swRedux.dispatch(
        requestCustomCategoriesSuccess({
            userCustomIndustries: customIndustries,
            sharedCustomIndustries,
        }),
    );
};

export async function loadCustomIndustriesMetadata(forceLoad = false, timeout = undefined) {
    try {
        const swRedux = SwRedux.getInstance();
        const { customCategories } = swRedux.getState();

        const isAlreadyLoading = customCategories?.isLoading;
        const hasData = customCategories?.customCategories;

        if (!forceLoad && (hasData || isAlreadyLoading)) {
            return true;
        }

        swRedux.dispatch(requestCustomCategories());

        const fetched = await UserCustomCategoryService.fetchCustomCategories(timeout).catch((error) => {
            Sentry.error("Error when fetching custom categories data", error);
            return null;
        });

        if (fetched) {
            await UserCustomCategoryService.init(fetched);
        }

        return true;
    } catch (e) {
        Sentry.error("Unhandled error in loadCustomIndustriesMetadata", e as Error);
        return true;
    }
}

const init = async (userCategories: ICustomCategoriesAndShared) => {
    customIndustries = userCategories.userCustomIndustries;
    sharedCustomIndustries = userCategories.sharedCustomIndustries;
    updateGlobalCustomCategoriesState();
};

const isCustomCategory = (name: string) =>
    Array.isArray(name)
        ? name?.some((n) => n.startsWith(CUSTOM_CATEGORY_PREFIX))
        : name?.startsWith(CUSTOM_CATEGORY_PREFIX);

export const brandCustomCategoryId = (name: string) => `${CUSTOM_CATEGORY_PREFIX}${name}`;

const resolveSharingState = (record: ICustomCategoryRecord): ESharingState => {
    const { sharedWithAccounts, sharedWithUsers } = record;
    if (sharedWithAccounts?.length > 0) {
        return ESharingState.ACCOUNT;
    }
    if (sharedWithUsers?.length > 0) {
        return ESharingState.SUBSET_OF_USERS;
    }
    return ESharingState.PRIVATE;
};

// @ts-ignore FIXME: strict migration - Type mismatch on sharedWithUsers/sharedWithAccounts (string[] | number[] vs number[])
const getCustomCategoryFromRecord: (record: ICustomCategoryRecord) => ICategory = (record) => {
    const { name, domains, categoryHash, lastUpdated, id, isShared, sharedWithAccounts, sharedWithUsers, segments } =
        record;
    return {
        ...record,
        id: brandCustomCategoryId(name),
        text: name,
        inactive: false,
        cssClass: "parent-category",
        domains: domains ?? [],
        categoryId: id.toString(),
        icon: "sw-icon-custom-categories",
        isCustomCategory: true,
        categoryHash: categoryHash ?? String(lastUpdated),
        categoryType: (record as any).categoryType,
        forApi: id,
        forUrl: brandCustomCategoryId(id),
        forDisplayApi: name,
        ...(!isShared && {
            sharingState: resolveSharingState(record),
            sharedWithAccounts: sharedWithAccounts?.map(Number),
            sharedWithUsers: sharedWithUsers?.map(Number),
        }),
        segments,
    };
};

const getCustomCategories: () => ICategory[] = () =>
    customIndustries.map(enrichCustomCategories(false)).map(getCustomCategoryFromRecord);

const getCustomCategoriesWithSegments: () => ICategory[] = () =>
    getCustomCategories().filter((category) => !!category.segments?.length);

const getSharedCustomCategories: () => ICategory[] = () => {
    if (enrichedSharedCustomIndustries.length !== sharedCustomIndustries.length) {
        enrichedSharedCustomIndustries = sharedCustomIndustries
            .map(enrichCustomCategories(true))
            .map(getCustomCategoryFromRecord);
    }
    return enrichedSharedCustomIndustries;
};

const enrichCustomCategories = (isShared: boolean) => (record: ICustomCategoryRecord) => {
    return {
        ...record,
        isShared,
    };
};

const getCustomAndSharedCategories = (): ICategory[] => [...getCustomCategories(), ...getSharedCustomCategories()];

const getCustomCategoriesRecords = () => customIndustries;

const getCustomCategoryByName = (name: string): ICategory | undefined => find(getCustomCategories(), { name });

const getCustomCategoryById = (id: string): ICategory | undefined =>
    getCustomCategories().find(({ categoryId }) => categoryId === id);

const getCustomAndSharedCategoryById = (id: string): ICategory | undefined =>
    getCustomAndSharedCategories().find(({ categoryId }) => categoryId === id);

const getCategoryByForUrl = (forUrl: string): ICategory | undefined => {
    const allCategories = getCustomAndSharedCategories();
    const category = allCategories.find((category) => category.forUrl === forUrl);
    return category;
};

const getCategoryHash = (categoryName: string, attribute: keyof ICategory) => {
    return getCustomCategories().find((customCategory) => customCategory[attribute] === categoryName)?.categoryHash;
};

const getCategoryHashByName = (categoryName: string) => {
    return getCategoryHash(categoryName, "text");
};

const getCategoryHashById = (categoryName: string) => {
    return getCategoryHash(categoryName, "id");
};

const getCustomCategoryByIdAsync = async (categoryId: string) => {
    const myCustomCategory = find(UserCustomCategoryService.getCustomCategories(), {
        categoryId,
    });

    if (myCustomCategory) {
        return myCustomCategory;
    } else {
        try {
            const data = (await getSharedCustomCategoriesSharedDashboard()) as [];
            return find(data, { id: categoryId });
        } catch (err) {
            return undefined;
        }
    }
};

const setCustomCategories = async (categories: ICustomCategories) => {
    customIndustries = categories.map(keysToCamelCase);
    updateGlobalCustomCategoriesState();
};

const getLatestIndustry = () => {
    const industries = getCustomCategories();
    const sortedIndustriesByAddedTime = industries.sort(({ addedTime }, { addedTime: addedTimeB }) => {
        return dayjs.utc(dayjs.utc(addedTimeB).unix()).diff(dayjs.utc(dayjs.utc(addedTime).unix()));
    });
    return sortedIndustriesByAddedTime?.[0];
};

const setDefaultSharingState = () => {
    const newIndustry = getLatestIndustry();
    updateSharingState(newIndustry, ESharingState.ACCOUNT, [window.similarweb.settings.user.accountId]);
};

const addCustomCategory = async (customCategory: FixmeType, url = `api/userdata/customIndustry`) => {
    const response: ICustomCategoryResponse[] = await DefaultFetchService.getInstance().post(url, {
        ...customCategory,
        AddedTime: new Date(),
    });
    try {
        await customCategoryResponseHandler(response);
        const hasSmartSharingPermission =
            window.similarweb.settings.components.IndustryAnalysis.resources.IndustriesSmartSharingFeature === "Open";
        // if the users don't have access to smart sharing,
        // we should update the industry state to shared with the entire organization

        if (!hasSmartSharingPermission) {
            await setDefaultSharingState();
        }
        return response;
    } catch (err) {
        Sentry.error("error in addCustomCategory in crmList.ts", err as Error);
    }
};

const addCustomCategoryFromSegmentCompatison = async (customCategory: FixmeType) => {
    const url = `api/userdata/segments/customsegments/group/createIndustry`;
    return addCustomCategory(customCategory, url);
};

const deleteCustomCategory = async (param: { id: string }) => {
    const url = `api/userdata/customIndustry?id=${param.id}`;
    const response = await DefaultFetchService.getInstance().delete(url);
    try {
        await customCategoryResponseHandler(response);
        updateGlobalCustomCategoriesState();
        return response;
    } catch (err) {
        Sentry.error("error in deleteCustomCategory in crmList.ts", err as Error);
    }
};

const updateCustomCategory = async (customCategory: FixmeType) => {
    const url = `api/userdata/customIndustry`;
    const response: ICustomCategoryResponse[] = await DefaultFetchService.getInstance().put(url, customCategory);
    try {
        await customCategoryResponseHandler(response);
        updateGlobalCustomCategoriesState();
        return response;
    } catch (err) {
        Sentry.error("error in updateCustomCategory in crmList.ts", err as Error);
    }
};

const customCategoryResponseHandler = async (response: FixmeType) => {
    await UserCustomCategoryService.setCustomCategories(response);
    // Some widgets resolve their category fresh inside queryFn, so only an explicit
    // invalidation triggers a refetch. Use "all" (not the default "active") since
    // refetchOnMount:false would otherwise skip unmounted widgets even after this.
    await queryClient.invalidateQueries({ refetchType: "all" });
};

//////// Private functions /////////
const fetchCustomCategories = (timeout?: number): Promise<ICustomCategoriesAndShared> => {
    const fetchService = DefaultFetchService.getInstance();
    // Let failures reject instead of masking them as an empty result (was overwriting good data on retry).
    return fetchService.get<ICustomCategoriesAndShared>(CUSTOM_CATEGORIES_ENDPOINT, undefined, {
        headers: NoCacheHeaders,
        ...(timeout ? { timeout } : {}),
    });
};

const keysToCamelCase = <T extends object>(obj: T) => {
    return mapKeys(obj, (value, key) => {
        if (EXCLUDE_CAMEL_CASE_KEYS.includes(key)) {
            return key;
        }
        return camelCase(key);
    }) as T;
};

type SplittedCategoriesResult<T> = {
    customIndustries: T[];
    sharedCustomIndustries: T[];
};

const splitCustomCategoriesArray = <T extends { categoryId: string }>(
    customCategories: T[],
): SplittedCategoriesResult<T> => {
    const myCustomCategoriesIds = new Set(getCustomCategories().map(({ categoryId }) => categoryId));
    const splittedCustomCategories = customCategories.reduce<SplittedCategoriesResult<T>>(
        (result, category) => {
            const { customIndustries, sharedCustomIndustries } = result;
            const isShared = !myCustomCategoriesIds.has(category.categoryId);
            return {
                customIndustries: isShared ? customIndustries : [...customIndustries, category],
                sharedCustomIndustries: isShared ? [...sharedCustomIndustries, category] : sharedCustomIndustries,
            };
        },
        { customIndustries: [], sharedCustomIndustries: [] },
    );
    return splittedCustomCategories;
};

const removeCategoryIdPrefix = (categoryId: string) => categoryId.slice(1);

const resolveSharingPayload = (
    industry: ICategory,
    sharingState: ESharingState,
    sharedWithUsers: Array<number>,
    sharedWithAccounts: Array<number>,
    notification: INotification,
) => {
    const { sharedWithUsers: currentSharedWithUsers } = industry;
    const newUsersIds = sharedWithUsers.filter((user) => !currentSharedWithUsers?.includes(user));
    const { shouldNotify, notificationContent } = notification;
    return {
        AccountIds: sharedWithAccounts,
        UserIds: sharedWithUsers,
        ...(shouldNotify &&
            sharingState === ESharingState.SUBSET_OF_USERS && {
                Message: notificationContent,
                NewUsersAdd: newUsersIds,
            }),
    };
};
const updateSharingState = async (
    industry: ICategory,
    sharingState: ESharingState,
    sharedWithAccounts: Array<number> = [],
    sharedWithUsers: Array<number> = [],
    notification: INotification = { shouldNotify: false },
) => {
    const SHARING_ENDPOINT = "api/userdata/customIndustry/share";
    const { categoryId } = industry;
    const fetchService = DefaultFetchService.getInstance();
    const payload = resolveSharingPayload(industry, sharingState, sharedWithUsers, sharedWithAccounts, notification);
    await fetchService.put(`${SHARING_ENDPOINT}/${categoryId}`, payload);
    customIndustries = customIndustries.map((industryItem) => {
        if (industryItem.id !== industry.categoryId) {
            return industryItem;
        }
        return {
            ...industryItem,
            sharedWithUsers: sharedWithUsers.map(String),
            sharedWithAccounts: sharedWithAccounts.map(String),
        };
    });
    updateGlobalCustomCategoriesState();
};

const isCustomCategoryNameAlreadyExists = (name: string) => {
    const userCategories = getCustomCategories();
    return userCategories.some((category) => category.name === name);
};

const cloneSharedCustomIndustry = (categoryId: CategoryId): Promise<ICustomCategoryResponse[] | undefined> => {
    const category = getCustomAndSharedCategoryById(categoryId);

    if (!category) {
        return Promise.reject(new Error(`Category with id ${categoryId} not found`));
    }

    const { name, domains, categoryType } = category;
    let newName = `${name} ${COPIED_INDUSTRY_SUFFIX}`;
    let index = 0;
    while (isCustomCategoryNameAlreadyExists(newName)) {
        index++;
        newName = `${name} ${COPIED_INDUSTRY_SUFFIX} ${index}`;
    }
    const categoryPromise = addCustomCategory({
        name: newName,
        domains,
        categoryType,
    });
    return categoryPromise;
};

const refreshCustomCategories = (): Promise<ICustomCategoriesAndShared> => {
    const fetchService = DefaultFetchService.getInstance();
    const customCategoriesPromise = fetchService.get<ICustomCategoriesAndShared>(
        CUSTOM_CATEGORIES_ENDPOINT,
        undefined,
        {
            headers: NoCacheHeaders,
        },
    );
    customCategoriesPromise.then((customCategories) => {
        init(customCategories);
    });
    return customCategoriesPromise;
};

export const UserCustomCategoryService = {
    init,
    isCustomCategory,
    getCustomCategories,
    getCustomCategoriesRecords,
    getCustomCategoryFromRecord,
    getCustomCategoryByName,
    getCustomCategoryById,
    getCategoryByForUrl,
    getCustomCategoryByIdAsync,
    getCategoryHash,
    getCategoryHashByName,
    getCategoryHashById,
    setCustomCategories,
    removeCategoryIdPrefix,
    addCustomCategory,
    addCustomCategoryFromSegmentCompatison,
    deleteCustomCategory,
    updateCustomCategory,
    getSharedCustomCategories,
    getCustomAndSharedCategoryById,
    getCustomAndSharedCategories,
    fetchCustomCategories,
    splitCustomCategoriesArray,
    updateSharingState,
    cloneSharedCustomIndustry,
    refreshCustomCategories,
    getLatestIndustry,
    getCustomCategoriesWithSegments,
};
